import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowDecision, FlowNote } from '../FlowPrimitives';

// ============================================================================
// Experiment 7 – Gravimetric Determination of Chloride and Phosphorus
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp7',
  title: 'Experiment 7: Gravimetric Determination of Chloride and Phosphorus',
  objectives: [
    'Apply gravimetric analysis principles to quantitative determination',
    'Determine chloride content by precipitation as AgCl',
    'Determine phosphorus content by precipitation as MgNH₄PO₄·6H₂O',
    'Use gravimetric factors for stoichiometric calculations',
  ],
  safetyNotes: [
    'AgNO₃ stains skin black — wear gloves at all times',
    'Concentrated HNO₃ is corrosive — handle in fume hood',
    'NH₃ vapors are irritating — work in well-ventilated area',
    'Hot oven — burn hazard; use tongs and heat-resistant gloves',
  ],
  equipment: [
    'Analytical balance',
    'Beakers (250 mL)',
    'Vacuum filtration apparatus',
    'Filter paper',
    'Drying oven (~100°C)',
    'Watch glass',
    'Volumetric flasks',
    'Stirring rod',
  ],
  chemicals: [
    'AgNO₃ solution',
    'Concentrated HNO₃',
    'Distilled water',
    'Acetone (for washing)',
    'MgSO₄·7H₂O solution',
    '2 M NH₃ solution',
    '75% isopropyl alcohol (wash solution)',
    'Fertilizer sample',
  ],
  procedure: [
    {
      number: 1,
      title: 'Prepare Sample (Chloride)',
      details: [
        'Weigh 0.2–0.4 g sample accurately on analytical balance',
        'Transfer to 250 mL beaker',
        'Dissolve in 150 mL distilled water',
        'Add 1 mL concentrated HNO₃ to acidify',
      ],
      safetyNote: 'HNO₃ is corrosive — add in fume hood',
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 2,
      title: 'Precipitate AgCl',
      details: [
        'Add ~20 mL AgNO₃ solution slowly with constant stirring',
        'Heat gently to coagulate the precipitate',
        'Test for complete precipitation: add a drop of AgNO₃ to clear supernatant',
        'If cloudiness appears, add more AgNO₃ and retest',
      ],
      safetyNote: 'AgNO₃ stains skin — wear gloves',
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 3,
      title: 'Filter Precipitate',
      details: [
        'Pre-weigh filter paper on analytical balance',
        'Set up vacuum filtration apparatus',
        'Filter the precipitate carefully',
      ],
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 4,
      title: 'Wash Precipitate',
      details: [
        'Wash precipitate with distilled water',
        'Follow with acetone wash',
        'Ensure all soluble impurities are removed',
      ],
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 5,
      title: 'Dry and Weigh',
      details: [
        'Dry at ~100°C in oven to constant mass',
        'Cool in desiccator before weighing',
        'Weigh filter paper + precipitate',
        'Repeat drying/weighing until constant mass achieved',
      ],
      safetyNote: 'Hot oven — use tongs, avoid burns',
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 6,
      title: 'Calculate %Cl',
      details: [
        'Mass AgCl = (filter + precipitate) – filter mass',
        '%Cl = (mass AgCl × 0.2473 / mass sample) × 100',
        'GF(Cl) = 35.45 / 143.32 = 0.2473',
      ],
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 7,
      title: 'Phosphorus in Fertilizer (Part II)',
      details: [
        'Weigh ~1.0 g fertilizer accurately',
        'Dissolve in 50 mL water; filter off insoluble material',
        'Add MgSO₄·7H₂O solution, then 2 M NH₃ slowly until pH > 9',
        'Stir to promote crystallization; allow precipitate to settle',
        'Filter on pre-weighed paper; wash with water then 75% isopropyl alcohol',
        'Dry at ~100°C to constant mass',
        'Calculate %P and %P₂O₅',
      ],
      safetyNote: 'NH₃ vapors are irritating — use fume hood',
      manualRef: 'Lab Manual p.2',
    },
  ],
  keyFormulas: [
    'GF = (MW analyte × a) / (MW precipitate × b)',
    '%Cl = (mass AgCl × 0.2473 / mass sample) × 100',
    'GF(Cl) = 35.45 / 143.32 = 0.2473',
    '%P = (mass precipitate × 0.1262 / mass fertilizer) × 100',
    '%P₂O₅ = (mass precipitate × 0.2891 / mass fertilizer) × 100',
  ],
  tips: [
    'Add AgNO₃ slowly — rapid addition creates fine particles hard to filter',
    'Heating helps coagulate the precipitate for easier filtration',
    'Always test for complete precipitation before filtering',
    'Dry to "constant mass" means successive weighings agree within 0.3 mg',
    'Wash MgNH₄PO₄ with water then 75% isopropyl alcohol to speed drying',
    'HNO₃ prevents precipitation of other silver salts (e.g., AgCO₃)',
  ],
};

// ============================================================================
// Flowchart — Part I: Chloride Analysis Procedure
// ============================================================================

function Flowchart() {
  // Layout constants — 600 wide canvas, nodes centered at x=210 (width 180)
  const nodeW = 180;
  const nodeH = 56;
  const cx = 210; // left edge of centred node
  const startY = 44;
  const gap = 76; // vertical spacing between nodes

  const row = (i: number) => startY + i * gap;

  return (
    <FlowContainer width={600} height={680} title="Part I — Gravimetric Chloride Analysis">
      {/* Start terminal */}
      <FlowTerminal x={230} y={row(0)} width={140} height={36} label="Start" color="#6366f1" />

      {/* Step 1 — Prepare Sample */}
      <FlowArrow from={{ x: 300, y: row(0) + 36 }} to={{ x: 300, y: row(1) }} />
      <FlowNode x={cx} y={row(1)} width={nodeW} height={nodeH} title="Prepare Sample" subtitle="Weigh, dissolve, add HNO₃" color="#0d9488" />

      {/* Step 2 — Add AgNO₃ */}
      <FlowArrow from={{ x: 300, y: row(1) + nodeH }} to={{ x: 300, y: row(2) }} />
      <FlowNode x={cx} y={row(2)} width={nodeW} height={nodeH} title="Add AgNO₃" subtitle="Slowly with stirring, heat" color="#0d9488" />

      {/* Decision — Complete precipitation? */}
      <FlowArrow from={{ x: 300, y: row(2) + nodeH }} to={{ x: 300, y: row(3) }} />
      <FlowDecision x={265} y={row(3)} size={70} label="Precip. complete?" color="#f59e0b" />

      {/* No branch loops back to Add AgNO₃ */}
      <FlowArrow
        from={{ x: 265, y: row(3) + 35 }}
        to={{ x: 200, y: row(3) + 35 }}
        label="No"
        color="#ef4444"
      />
      <FlowArrow
        from={{ x: 200, y: row(3) + 35 }}
        to={{ x: 200, y: row(2) + 28 }}
        color="#ef4444"
      />
      <FlowArrow
        from={{ x: 200, y: row(2) + 28 }}
        to={{ x: cx, y: row(2) + 28 }}
        color="#ef4444"
      />

      {/* Yes branch continues */}
      <FlowArrow from={{ x: 300, y: row(3) + 70 }} to={{ x: 300, y: row(4) + 10 }} label="Yes" />

      {/* Step 3 — Filter */}
      <FlowNode x={cx} y={row(4) + 10} width={nodeW} height={nodeH} title="Filter Precipitate" subtitle="Vacuum filtration" color="#0d9488" />

      {/* Step 4 — Wash */}
      <FlowArrow from={{ x: 300, y: row(4) + 10 + nodeH }} to={{ x: 300, y: row(5) + 10 }} />
      <FlowNode x={cx} y={row(5) + 10} width={nodeW} height={nodeH} title="Wash Precipitate" subtitle="Water, then acetone" color="#0d9488" />

      {/* Step 5 — Dry / Heat */}
      <FlowArrow from={{ x: 300, y: row(5) + 10 + nodeH }} to={{ x: 300, y: row(6) + 10 }} />
      <FlowNode x={cx} y={row(6) + 10} width={nodeW} height={nodeH} title="Dry / Heat" subtitle="~100°C to constant mass" color="#0d9488" />
      <FlowNote x={400} y={row(6) + 14} text="Constant mass ±0.3 mg" width={140} color="#f59e0b" />

      {/* Step 6 — Weigh & Calculate */}
      <FlowArrow from={{ x: 300, y: row(6) + 10 + nodeH }} to={{ x: 300, y: row(7) + 10 }} />
      <FlowNode x={cx} y={row(7) + 10} width={nodeW} height={nodeH} title="Weigh & Calculate" subtitle="%Cl = AgCl × 0.2473 / sample" color="#0d9488" />

      {/* End terminal */}
      <FlowArrow from={{ x: 300, y: row(7) + 10 + nodeH }} to={{ x: 300, y: row(7) + 10 + nodeH + 20 }} />
      <FlowTerminal x={230} y={row(7) + 10 + nodeH + 20} width={140} height={36} label="Done" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Default Export
// ============================================================================

export default function Exp7Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
